import { useState, useEffect } from 'react';
import { useDispatch } from 'react-redux';
import db from '../../db/db';
import { setAlert } from '../../actions/alert';
import CurrencyInput from 'react-currency-input-field';
import ModalExtras from './ModalExtras';
import Ticket from './Ticket';

// ─── Estilo visual por método de pago ───────────────────────────────────────
const METODO_ESTILOS = [
    { keywords: ['efectivo', 'cash'],                   icon: 'fa-money-bill-wave',       color: '#48BB78', bg: 'rgba(72,187,120,0.12)' },
    { keywords: ['tarjeta', 'débito', 'debito', 'crédito', 'credito', 'visa', 'master'], icon: 'fa-credit-card', color: '#63B3ED', bg: 'rgba(99,179,237,0.12)' },
    { keywords: ['transfer', 'banco', 'spei', 'bbva', 'cuenta'], icon: 'fa-university',   color: '#B794F4', bg: 'rgba(183,148,244,0.12)' },
    { keywords: ['vale', 'vales', 'ticket'],             icon: 'fa-ticket-alt',            color: '#F6AD55', bg: 'rgba(246,173,85,0.12)' },
    { keywords: ['qr', 'clip', 'mercado', 'pago'],       icon: 'fa-qrcode',               color: '#F5C800', bg: 'rgba(245,200,0,0.12)' },
];

const getMetodoEstilo = (nombre = '') => {
    const n = nombre.toLowerCase();
    const match = METODO_ESTILOS.find(e => e.keywords.some(k => n.includes(k)));
    return match || { icon: 'fa-circle-dot', color: '#C0C0C0', bg: 'rgba(192,192,192,0.08)' };
};

// ─── Componente principal ────────────────────────────────────────────────────
const Venta = () => {
    const dispatch = useDispatch();

    // Catálogos
    const [productos, setProductos] = useState([]);
    const [metodos, setMetodos] = useState([]);
    const [empleadoLogueado, setEmpleadoLogueado] = useState(null);
    const [empleadosAsistieron, setEmpleadosAsistieron] = useState([]);
    const [eventoActivo, setEventoActivo] = useState(null);

    // Carrito
    const [carrito, setCarrito] = useState([]);

    // Selecciones
    const [metodoData, setMetodoData] = useState(null);

    // Modal extras
    const [productoExtras, setProductoExtras] = useState(null);
    const [itemUidExtras, setItemUidExtras] = useState(null);

    // Status
    const [statusProcesar, setStatusProcesar] = useState(false);
    const [mostrarTicket, setMostrarTicket] = useState(false);
    const [ticketData, setTicketData] = useState(null);

    // Modo precio especial
    const [modoPrecio, setModoPrecio] = useState(null); // null | 'cortesia' | 'especial'

    // Calculadora de feria
    const [cobrarCon, setCobrarCon] = useState('');
    const [mostrarFeria, setMostrarFeria] = useState(false);

    // Filtro productos
    const [busqueda, setBusqueda] = useState('');

    useEffect(() => {
        const init = async () => {
            const sesion = await db.sesion.toArray();
            const empleado = sesion[0];
            setEmpleadoLogueado(empleado);

            const mets = await db.metodos.toArray();
            const sucursalId = empleado.sucursal?._id || empleado.sucursal;
            const sucursalReg = await db.sucursales
                .filter(s => String(s._id || s.id) === String(sucursalId))
                .first();
            setEventoActivo(sucursalReg?.evento || null);

            const rawProds = await db.productos.toArray();
            const prods = rawProds
                .map(p => {
                    const entrada = p.precios?.find(pr =>
                        String(pr.sucursal?._id || pr.sucursal) === String(sucursalId)
                    );
                    return entrada ? { ...p, precio: entrada.precio } : p;
                })
                .sort((a, b) => {
                    if (a.tipo !== b.tipo) return a.tipo - b.tipo;
                    return a.nombre.localeCompare(b.nombre, 'es');
                });

            setProductos(prods);
            setMetodos(mets.map(m => ({ value: m._id, label: m.nombre, ...m })));

            const todosAsistencias = await db.asistencias.toArray();
            const paseReciente = todosAsistencias.sort((a, b) => new Date(b.fecha) - new Date(a.fecha))[0] || null;

            if (paseReciente) {
                setEmpleadosAsistieron(paseReciente.empleados.filter(e => e.asistio === true));
            }
        };

        init();
    }, []);

    // ── Carrito ──────────────────────────────────────────────────────────────
    const handleAgregarProducto = (producto) => {
        setCarrito(prev => [...prev, {
            uid: Date.now() + Math.random(),
            producto_id: producto._id,
            producto_ref: producto,
            nombre: producto.nombre,
            precio_base: producto.precio,
            extras: [],
            precio_total: producto.precio,
            cantidad: 1,
        }]);
    };

    const handleCantidad = (uid, delta) => {
        setCarrito(prev => prev.map(item =>
            item.uid === uid
                ? { ...item, cantidad: Math.max(1, item.cantidad + delta) }
                : item
        ));
    };

    const handleEliminar = (uid) => {
        setCarrito(prev => prev.filter(item => item.uid !== uid));
    };

    const handleAbrirExtras = (item) => {
        setProductoExtras(item.producto_ref);
        setItemUidExtras(item.uid);
    };

    const handleGuardarExtras = (extrasSeleccionados) => {
        const precioExtras = extrasSeleccionados.reduce((acc, e) => acc + e.precio, 0);
        setCarrito(prev => prev.map(item =>
            item.uid === itemUidExtras
                ? { ...item, extras: extrasSeleccionados, precio_total: item.precio_base + precioExtras }
                : item
        ));
        setProductoExtras(null);
        setItemUidExtras(null);
    };

    const totalGeneral = carrito.reduce((acc, item) => acc + (item.precio_total * item.cantidad), 0);
    const eventoCobrado = eventoActivo ? eventoActivo.cobrado : true;
    const factorPrecio = modoPrecio === 'cortesia' ? 0 : modoPrecio === 'especial' ? 0.5 : 1;
    const totalFinal = totalGeneral * factorPrecio;

    const esEfectivo = metodoData?.label?.toLowerCase().includes('efectivo') ?? false;
    const cobrarConNum = parseFloat(cobrarCon) || 0;
    const cambio = cobrarConNum - totalFinal;
    const mostrarCalculadora = eventoCobrado && esEfectivo && carrito.length > 0 && modoPrecio !== 'cortesia';

    // ── Procesar venta ───────────────────────────────────────────────────────
    const handleProcesarVenta = async () => {
        if (carrito.length === 0) return dispatch(setAlert('Agrega productos al carrito', 'danger'));
        if (eventoCobrado && modoPrecio !== 'cortesia' && !metodoData) return dispatch(setAlert('Selecciona un método de pago', 'danger'));

        setStatusProcesar(true);

        const letras = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ';
        const folio = letras[Math.floor(Math.random() * 26)] +
                      letras[Math.floor(Math.random() * 26)] +
                      Math.floor(Math.random() * 900 + 100);

        const todosEmpleados = await db.empleados.toArray();
        const empleadosVenta = empleadosAsistieron
            .map(emp => {
                const registro = todosEmpleados.find(e => e._id === emp.empleado);
                const tipo = registro?.tipo ?? null;
                let totalComision = 0;
                if (modoPrecio !== 'cortesia' && modoPrecio !== 'especial') {
                    carrito.forEach(item => {
                        const prod = item.producto_ref;
                        const comisionPersonal = prod.comisiones_empleado?.find(c => c.empleado === emp.empleado);
                        const comisionTipo     = prod.comisiones?.find(c => c.tipo === tipo);
                        const comision         = comisionPersonal ?? comisionTipo;
                        if (comision) totalComision += comision.cantidad * item.cantidad;
                    });
                }
                return { empleado: emp.empleado, tipo, total_comision: totalComision };
            })
            .filter(emp => emp.tipo !== 4);

        const ventaLocal = {
            sucursal:    empleadoLogueado.sucursal?._id || empleadoLogueado.sucursal,
            metodo:      modoPrecio === 'cortesia' ? null : (metodoData ? metodoData._id : null),
            vendedor:    empleadoLogueado._id,
            evento:      eventoActivo?._id || null,
            folio,
            fecha:       new Date(),
            total:       totalFinal,
            status:      true,
            cortesia:    modoPrecio === 'cortesia',
            especial:    modoPrecio === 'especial',
            empleados:   empleadosVenta
        };

        const ventaId = await db.ventas.add(ventaLocal);

        for (const item of carrito) {
            await db.ventas_productos.add({
                venta_local_id: ventaId,
                producto:       item.producto_id,
                cantidad:       item.cantidad,
                precio:         item.precio_total * factorPrecio,
                extras:         item.extras.map(e => ({ extra: e._id, nombre: e.nombre, precio: e.precio * factorPrecio })),
                cortesia:       modoPrecio === 'cortesia',
                especial:       modoPrecio === 'especial',
            });
        }

        setTicketData({
            folio,
            fecha:     new Date(),
            vendedor:  `${empleadoLogueado.nombre || ''} ${empleadoLogueado.apellido_paterno || ''}`.trim(),
            sucursal:  empleadoLogueado.sucursal?.nombre || '',
            metodo:    modoPrecio === 'cortesia' ? null : (metodoData?.label || null),
            cobrado:   eventoCobrado,
            carrito:   carrito.map(item => ({
                ...item,
                precio_total: item.precio_total * factorPrecio,
                extras: item.extras.map(e => ({ ...e, precio: e.precio * factorPrecio })),
            })),
            total:     totalFinal,
        });

        setMostrarTicket(true);
        setStatusProcesar(false);
    };

    const handleReset = () => {
        setCarrito([]);
        setMetodoData(null);
        setModoPrecio(null);
        setCobrarCon('');
        setMostrarFeria(false);
        setMostrarTicket(false);
        setTicketData(null);
    };

    const handleCancelar = () => {
        setCarrito([]);
        setMetodoData(null);
        setModoPrecio(null);
        setCobrarCon('');
        setMostrarFeria(false);
        setBusqueda('');
    };

    // ── Productos filtrados ──────────────────────────────────────────────────
    const productosFiltrados = busqueda
        ? productos.filter(p => p.nombre.toLowerCase().includes(busqueda.toLowerCase()))
        : productos;

    // ── Render ───────────────────────────────────────────────────────────────
    return (
        <>
            <style>{`
                .prod-btn {
                    background: #1A1A1A;
                    border: 1px solid #2E2E2E;
                    border-radius: 12px;
                    padding: 1.2rem 1rem;
                    text-align: left;
                    width: 100%;
                    color: #F0F0F0;
                    cursor: pointer;
                    transition: border-color 0.15s, background 0.15s;
                    display: flex;
                    flex-direction: column;
                    gap: 0.4rem;
                }
                .prod-btn:hover { border-color: #F5C800; background: rgba(245,200,0,0.04); }
                .prod-btn:active { background: rgba(245,200,0,0.08); }
                .carrito-fila {
                    display: flex;
                    align-items: flex-start;
                    gap: 0.85rem;
                    padding: 1.1rem 1rem;
                    border-bottom: 1px solid #222;
                }
                .carrito-fila:last-child { border-bottom: none; }
                .btn-qty {
                    width: 46px; height: 46px;
                    background: #222;
                    border: 1px solid #333;
                    border-radius: 10px;
                    color: #F0F0F0;
                    font-size: 1.4rem;
                    display: flex; align-items: center; justify-content: center;
                    cursor: pointer;
                    flex-shrink: 0;
                    transition: background 0.15s;
                }
                .btn-qty:hover { background: #2E2E2E; }
            `}</style>

            <div style={styles.page}>
                <div style={styles.layout}>

                    {/* ── Panel izquierdo: Productos ── */}
                    <div style={styles.panelProductos}>
                        <div style={styles.sectionHeader}>
                            <span>Productos</span>
                            <span style={{ color: '#555', fontSize: '0.85rem' }}>
                                {productosFiltrados.length}
                            </span>
                        </div>

                        <input
                            type="text"
                            value={busqueda}
                            onChange={e => setBusqueda(e.target.value)}
                            placeholder="Buscar producto..."
                            style={styles.buscador}
                        />

                        <div style={styles.gridProductos}>
                            {productosFiltrados.reduce((acc, prod, idx) => {
                                const anterior = productosFiltrados[idx - 1];
                                if (idx > 0 && prod.tipo !== anterior.tipo) {
                                    const ETIQUETAS = { 0: 'Alimentos', 1: 'Bebidas', 2: 'Extras' };
                                    acc.push(
                                        <div key={`sep-${prod.tipo}`} style={styles.separadorTipo}>
                                            <hr style={styles.separadorLinea} />
                                            <span style={styles.separadorLabel}>
                                                {ETIQUETAS[prod.tipo] ?? `Tipo ${prod.tipo}`}
                                            </span>
                                            <hr style={styles.separadorLinea} />
                                        </div>
                                    );
                                }
                                acc.push(
                                    <button
                                        key={prod._id || prod.id}
                                        className="prod-btn"
                                        onClick={() => handleAgregarProducto(prod)}
                                    >
                                        <span style={{ fontWeight: 600, fontSize: '1.85rem', lineHeight: 1.25 }}>
                                            {prod.nombre}
                                        </span>
                                        <span style={{ color: '#F5C800', fontWeight: 700, fontSize: '1.75rem' }}>
                                            ${(prod.precio || 0).toFixed(2)}
                                        </span>
                                    </button>
                                );
                                return acc;
                            }, [])}
                            {productosFiltrados.length === 0 && (
                                <p style={{ color: '#555', fontSize: '0.9rem', gridColumn: '1/-1' }}>
                                    Sin resultados
                                </p>
                            )}
                        </div>
                    </div>

                    {/* ── Panel derecho: Carrito + Pago ── */}
                    <div style={styles.panelCarrito}>

                        {/* Carrito */}
                        <div style={styles.sectionHeader}>
                            <span>
                                {carrito.length > 0 && (
                                    <span style={{ color: '#F5C800', fontWeight: 700, marginLeft: '0.6rem', fontSize: '0.9rem' }}>
                                        {carrito.reduce((a, i) => a + i.cantidad, 0)} items
                                    </span>
                                )}
                            </span>
                            {(empleadoLogueado?.cortesia || empleadoLogueado?.especial) && (
                                <div style={{ display: 'flex', gap: '0.4rem' }}>
                                    {empleadoLogueado?.cortesia && (
                                        <button
                                            style={modoPrecio === 'cortesia' ? styles.btnModoActivo : styles.btnModo}
                                            onClick={() => setModoPrecio(prev => prev === 'cortesia' ? null : 'cortesia')}
                                        >
                                            <i className="fas fa-gift me-1"></i>
                                            Cortesía
                                        </button>
                                    )}
                                    {empleadoLogueado?.especial && (
                                        <button
                                            style={modoPrecio === 'especial' ? styles.btnModoActivo : styles.btnModo}
                                            onClick={() => setModoPrecio(prev => prev === 'especial' ? null : 'especial')}
                                        >
                                            <i className="fas fa-tag me-1"></i>
                                            Especial
                                        </button>
                                    )}
                                </div>
                            )}
                        </div>

                        <div style={styles.carritoBox}>
                            {carrito.length === 0 ? (
                                <div style={styles.carritoVacio}>
                                    <i className="fas fa-shopping-cart" style={{ fontSize: '2rem', color: '#333', marginBottom: '0.5rem' }}></i>
                                    <p style={{ color: '#555', margin: 0, fontSize: '0.9rem' }}>
                                        Agrega productos al carrito
                                    </p>
                                </div>
                            ) : (
                                carrito.map(item => (
                                    <div key={item.uid} className="carrito-fila">
                                        {/* Info */}
                                        <div style={{ flex: 1, minWidth: 0 }}>
                                            <div style={{ color: '#F0F0F0', fontWeight: 600, fontSize: '1.25rem' }}>
                                                {item.nombre}
                                            </div>
                                            {item.extras.length > 0 && (
                                                <div style={{ color: '#888', fontSize: '1rem', marginTop: '0.2rem' }}>
                                                    {item.extras.map(e => e.nombre).join(', ')}
                                                </div>
                                            )}
                                            <div style={{ color: '#F5C800', fontWeight: 700, fontSize: '1.15rem', marginTop: '0.25rem' }}>
                                                ${(item.precio_total * item.cantidad).toFixed(2)}
                                            </div>
                                        </div>

                                        {/* Controles */}
                                        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: '0.5rem', flexShrink: 0 }}>
                                            {/* Cantidad */}
                                            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                                                <button className="btn-qty" onClick={() => handleCantidad(item.uid, -1)}>−</button>
                                                <span style={{ color: '#F0F0F0', fontWeight: 700, minWidth: '28px', textAlign: 'center', fontSize: '1.2rem' }}>
                                                    {item.cantidad}
                                                </span>
                                                <button className="btn-qty" onClick={() => handleCantidad(item.uid, +1)}>+</button>
                                            </div>

                                            {/* Extras + Eliminar */}
                                            <div style={{ display: 'flex', gap: '0.4rem' }}>
                                                {item.producto_ref?.extras?.length > 0 && (
                                                    <button
                                                        style={styles.btnExtras}
                                                        onClick={() => handleAbrirExtras(item)}
                                                    >
                                                        <i className="fas fa-plus-circle me-1"></i>
                                                        Extras
                                                    </button>
                                                )}
                                                <button style={styles.btnEliminar} onClick={() => handleEliminar(item.uid)}>
                                                    <i className="fas fa-trash"></i>
                                                </button>
                                            </div>
                                        </div>
                                    </div>
                                ))
                            )}
                        </div>

                        {/* Total */}
                        {carrito.length > 0 && (
                            <div style={styles.totalBox}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '0.6rem' }}>
                                    <span style={{ color: '#888', fontSize: '0.95rem' }}>Total</span>
                                    {mostrarCalculadora && (
                                        <button
                                            style={styles.btnFeria}
                                            onClick={() => setMostrarFeria(true)}
                                            title="Calcular cambio"
                                        >
                                            <i className="fas fa-coins me-1"></i>
                                            Cambio
                                        </button>
                                    )}
                                </div>
                                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: '0.1rem' }}>
                                    {modoPrecio && (
                                        <span style={{ color: '#888', fontSize: '0.8rem', textDecoration: 'line-through' }}>
                                            ${totalGeneral.toFixed(2)}
                                        </span>
                                    )}
                                    <span style={{ color: modoPrecio ? '#48BB78' : '#F5C800', fontWeight: 800, fontSize: '1.4rem' }}>
                                        ${totalFinal.toFixed(2)}
                                    </span>
                                </div>
                            </div>
                        )}

                        {/* Métodos de pago */}
                        {eventoCobrado && (
                            <div style={{ marginTop: '1rem', display: 'flex', gap: '0.5rem' }}>
                                {metodos.map(m => {
                                    const { icon, color, bg } = getMetodoEstilo(m.label);
                                    const activo = metodoData?._id === m._id;
                                    return (
                                        <button
                                            key={m._id}
                                            onClick={() => { setMetodoData(activo ? null : m); setCobrarCon(''); }}
                                            style={{
                                                flex: 1,
                                                background: activo ? bg : '#141414',
                                                border: activo ? `2px solid ${color}` : '1.5px solid #2E2E2E',
                                                borderRadius: '12px',
                                                padding: '0.75rem 0.4rem',
                                                color: activo ? color : '#444',
                                                fontWeight: activo ? 800 : 600,
                                                fontSize: '0.78rem',
                                                cursor: 'pointer',
                                                display: 'flex',
                                                flexDirection: 'column',
                                                alignItems: 'center',
                                                gap: '0.4rem',
                                                transition: 'all 0.15s',
                                                lineHeight: 1.2,
                                                textAlign: 'center',
                                            }}
                                        >
                                            <i className={`fas ${icon}`} style={{ fontSize: '1.3rem' }}></i>
                                            {m.label}
                                        </button>
                                    );
                                })}
                            </div>
                        )}

                        {/* Botones acción */}
                        <div style={{ display: 'flex', gap: '0.6rem', marginTop: '0.75rem' }}>
                            <button
                                style={carrito.length === 0 && !metodoData
                                    ? { ...styles.btnCancelar, opacity: 0.35, cursor: 'not-allowed' }
                                    : { ...styles.btnCancelar, flex: 1 }
                                }
                                onClick={handleCancelar}
                                disabled={carrito.length === 0 && !metodoData}
                            >
                                <i className="fas fa-times me-2"></i>
                                Cancelar
                            </button>
                            <button
                                style={statusProcesar || carrito.length === 0
                                    ? { ...styles.btnProcesar, opacity: 0.5, cursor: 'not-allowed', flex: 1 }
                                    : { ...styles.btnProcesar, flex: 1 }
                                }
                                onClick={handleProcesarVenta}
                                disabled={statusProcesar || carrito.length === 0}
                            >
                                {statusProcesar ? (
                                    <>
                                        <span className="spinner-border spinner-border-sm me-2" role="status" />
                                        Procesando...
                                    </>
                                ) : (
                                    <>
                                        <i className="fas fa-check-circle me-2"></i>
                                        Procesar
                                    </>
                                )}
                            </button>
                        </div>
                    </div>
                </div>
            </div>

            {/* Modal extras */}
            {productoExtras && (
                <ModalExtras
                    producto={productoExtras}
                    extrasSeleccionados={carrito.find(i => i.uid === itemUidExtras)?.extras || []}
                    onGuardar={handleGuardarExtras}
                    onCerrar={() => { setProductoExtras(null); setItemUidExtras(null); }}
                />
            )}

            {/* Ticket */}
            {mostrarTicket && ticketData && (
                <Ticket data={ticketData} onCerrar={handleReset} />
            )}

            {/* Modal cambio */}
            {mostrarFeria && (
                <div style={feriaModal.overlay} onClick={() => { setMostrarFeria(false); setCobrarCon(''); }}>
                    <div style={feriaModal.card} onClick={e => e.stopPropagation()}>
                        <div style={feriaModal.header}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '0.6rem' }}>
                                <i className="fas fa-coins" style={{ color: '#F5C800', fontSize: '1.1rem' }}></i>
                                <span style={feriaModal.titulo}>Calculadora de cambio</span>
                            </div>
                            <button style={feriaModal.btnCerrar} onClick={() => { setMostrarFeria(false); setCobrarCon(''); }}>
                                <i className="fas fa-times"></i>
                            </button>
                        </div>

                        <div style={feriaModal.body}>
                            {/* Total a cobrar */}
                            <div style={feriaModal.totalFila}>
                                <span style={feriaModal.etiqueta}>Total</span>
                                <span style={feriaModal.totalValor}>${totalFinal.toFixed(2)}</span>
                            </div>

                            {/* Input con cuanto paga */}
                            <div style={feriaModal.inputGrupo}>
                                <label style={feriaModal.etiqueta}>El cliente paga con</label>
                                <CurrencyInput
                                    value={cobrarCon}
                                    onValueChange={val => setCobrarCon(val || '')}
                                    prefix="$"
                                    decimalsLimit={2}
                                    groupSeparator=","
                                    decimalSeparator="."
                                    placeholder="$0.00"
                                    style={feriaModal.input}
                                    autoFocus
                                />
                            </div>

                            {/* Resultado */}
                            {cobrarConNum > 0 && (
                                <div style={cambio >= 0 ? feriaModal.resultOk : feriaModal.resultError}>
                                    <span style={feriaModal.resultLabel}>
                                        {cambio >= 0 ? 'Cambio a regresar' : 'Monto insuficiente'}
                                    </span>
                                    <span style={{ ...feriaModal.resultValor, color: cambio >= 0 ? '#48BB78' : '#E53E3E' }}>
                                        {cambio >= 0
                                            ? `$${cambio.toFixed(2)}`
                                            : `Faltan $${Math.abs(cambio).toFixed(2)}`
                                        }
                                    </span>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            )}
        </>
    );
};

const styles = {
    page: {
        height: 'calc(95vh - 75px)',
        overflow: 'hidden',
    },
    layout: {
        display: 'grid',
        gridTemplateColumns: '1fr 500px',
        gap: '1.25rem',
        height: '100%',
    },
    panelProductos: {
        display: 'flex',
        flexDirection: 'column',
        gap: '0.75rem',
        overflow: 'hidden',
    },
    panelCarrito: {
        display: 'flex',
        flexDirection: 'column',
        gap: '0',
        background: '#1A1A1A',
        border: '1px solid #2E2E2E',
        borderRadius: '14px',
        padding: '1.1rem',
        overflow: 'hidden',
    },
    sectionHeader: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        color: '#F0F0F0',
        fontWeight: 700,
        fontSize: '1rem',
        paddingBottom: '0.6rem',
        borderBottom: '1px solid #2E2E2E',
        marginBottom: '0.1rem',
        flexShrink: 0,
    },
    buscador: {
        background: '#1F1F1F',
        border: '1px solid #2E2E2E',
        borderRadius: '10px',
        padding: '0.65rem 1rem',
        color: '#F0F0F0',
        fontSize: '0.95rem',
        width: '100%',
        outline: 'none',
        flexShrink: 0,
    },
    gridProductos: {
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fill, minmax(210px, 1fr))',
        gap: '0.75rem',
        overflowY: 'auto',
        flex: 1,
        paddingRight: '4px',
        alignContent: 'start',
    },
    carritoBox: {
        flex: 1,
        overflowY: 'auto',
        margin: '0.5rem -1.1rem 0',
        padding: '0 1.1rem',
    },
    carritoVacio: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '2.5rem 0',
    },
    totalBox: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        padding: '0.75rem 0',
        borderTop: '1px solid #2E2E2E',
        marginTop: '0.5rem',
        flexShrink: 0,
    },
    btnExtras: {
        background: 'transparent',
        border: '1px solid rgba(245,200,0,0.5)',
        borderRadius: '8px',
        padding: '0.45rem 0.85rem',
        color: '#F5C800',
        fontSize: '1rem',
        cursor: 'pointer',
    },
    btnEliminar: {
        background: 'transparent',
        border: '1px solid rgba(245,200,0,0.5)',
        borderRadius: '8px',
        padding: '0.45rem 0.75rem',
        color: '#888',
        fontSize: '1rem',
        cursor: 'pointer',
    },
    separadorTipo: {
        gridColumn: '1 / -1',
        display: 'flex',
        alignItems: 'center',
        gap: '0.75rem',
        margin: '0.25rem 0',
    },
    separadorLinea: {
        flex: 1,
        borderColor: '#2E2E2E',
        margin: 0,
    },
    separadorLabel: {
        color: '#555',
        fontSize: '0.75rem',
        fontWeight: 700,
        textTransform: 'uppercase',
        letterSpacing: '1px',
        whiteSpace: 'nowrap',
    },
    btnFeria: {
        background: 'transparent',
        border: '1px solid #2E2E2E',
        borderRadius: '8px',
        padding: '0.3rem 0.7rem',
        color: '#888',
        fontSize: '0.82rem',
        fontWeight: 700,
        cursor: 'pointer',
        transition: 'border-color 0.15s, color 0.15s',
    },
    btnModo: {
        flex: 1,
        minWidth: '150px',
        background: 'transparent',
        border: '1px solid #2E2E2E',
        borderRadius: '10px',
        padding: '0.55rem 1rem',
        color: '#888',
        fontWeight: 700,
        fontSize: '0.9rem',
        cursor: 'pointer',
        transition: 'border-color 0.15s, color 0.15s',
    },
    btnModoActivo: {
        flex: 1,
        minWidth: '150px',
        background: 'rgba(72,187,120,0.1)',
        border: '1px solid rgba(72,187,120,0.5)',
        borderRadius: '10px',
        padding: '0.55rem 1rem',
        color: '#48BB78',
        fontWeight: 700,
        fontSize: '0.9rem',
        cursor: 'pointer',
    },
    btnCancelar: {
        background: '#eb3508',
        color: '#ffffff',
        border: 'none',
        borderRadius: '12px',
        padding: '0.9rem',
        fontWeight: 800,
        fontSize: '1.05rem',
        cursor: 'pointer',
        marginTop: '0.75rem',
        flexShrink: 0,
        transition: 'border-color 0.2s, color 0.2s',
    },
    btnProcesar: {
        background: '#F5C800',
        color: '#111',
        border: 'none',
        borderRadius: '12px',
        padding: '0.9rem',
        fontWeight: 800,
        fontSize: '1.05rem',
        width: '100%',
        cursor: 'pointer',
        marginTop: '0.75rem',
        flexShrink: 0,
        transition: 'background 0.2s',
    },
};

const feriaModal = {
    overlay: {
        position: 'fixed',
        inset: 0,
        background: 'rgba(0,0,0,0.75)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 9999,
        padding: '1rem',
    },
    card: {
        background: '#1A1A1A',
        border: '1px solid #2E2E2E',
        borderRadius: '16px',
        width: '100%',
        maxWidth: '380px',
        overflow: 'hidden',
    },
    header: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        padding: '1rem 1.25rem',
        borderBottom: '1px solid #2E2E2E',
        background: '#111',
    },
    titulo: {
        color: '#F0F0F0',
        fontWeight: 700,
        fontSize: '1rem',
    },
    btnCerrar: {
        background: 'transparent',
        border: 'none',
        color: '#555',
        fontSize: '1rem',
        cursor: 'pointer',
        padding: '0.25rem 0.4rem',
    },
    body: {
        padding: '1.5rem 1.25rem',
        display: 'flex',
        flexDirection: 'column',
        gap: '1.25rem',
    },
    totalFila: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        background: '#111',
        border: '1px solid #2E2E2E',
        borderRadius: '10px',
        padding: '0.75rem 1rem',
    },
    etiqueta: {
        color: '#666',
        fontWeight: 700,
        fontSize: '0.8rem',
        textTransform: 'uppercase',
        letterSpacing: '0.5px',
    },
    totalValor: {
        color: '#F5C800',
        fontWeight: 800,
        fontSize: '1.4rem',
    },
    inputGrupo: {
        display: 'flex',
        flexDirection: 'column',
        gap: '0.5rem',
    },
    input: {
        background: '#111',
        border: '1px solid #3E3E3E',
        borderRadius: '10px',
        outline: 'none',
        color: '#F0F0F0',
        fontWeight: 700,
        fontSize: '1.6rem',
        width: '100%',
        padding: '0.65rem 0.85rem',
    },
    resultOk: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        background: 'rgba(72,187,120,0.08)',
        border: '1px solid rgba(72,187,120,0.3)',
        borderRadius: '10px',
        padding: '0.85rem 1rem',
    },
    resultError: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        background: 'rgba(229,62,62,0.08)',
        border: '1px solid rgba(229,62,62,0.3)',
        borderRadius: '10px',
        padding: '0.85rem 1rem',
    },
    resultLabel: {
        color: '#888',
        fontWeight: 600,
        fontSize: '0.85rem',
    },
    resultValor: {
        fontWeight: 800,
        fontSize: '1.5rem',
        color: 'inherit',
    },
};

export default Venta;
